import React, { useEffect } from 'react';
import {
  CircularProgress,
  Alert,
} from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid';
import { DataGrid, PageTitle, ListLayoutPanel, AddButton, GridEditButton } from '../../../components/common';
import { EditProcedencia } from './components/EditProcedencia';
import { type Procedencia, type ProcedenciaRequest } from './types/procedencia';
import { useProcedenciaStore } from './store/procedenciaStore';

const ProcedenciasPage: React.FC = () => {
  const {
    procedencias,
    loading,
    error,
    selectedProcedencia,
    dialogOpen,
    fetchProcedencias,
    saveProcedencia,
    setSelectedProcedencia,
    openDialog,
    closeDialog,
    clearError
  } = useProcedenciaStore();

  const columns: GridColDef[] = [
    { field: 'idProcedencia', headerName: 'ID', width: 80 },
    { field: 'descripcion', headerName: 'Descripción', width: 250, flex: 1 },
    { field: 'observacion', headerName: 'Observación', width: 300, flex: 1 },
    {
      field: 'activo',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => (
        <span style={{ color: params.row.activo ? 'green' : 'red' }}>
          {params.row.activo ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <GridEditButton
          onClick={() => handleEdit(params.row.idProcedencia)}
          ariaLabel={`Editar procedencia ${params.row.descripcion}`}
        />
      ),
    },
  ];

  useEffect(() => {
    fetchProcedencias();
  }, [fetchProcedencias]);

  const handleEdit = (id: number) => {
    const procedencia = procedencias.find(p => p.idProcedencia === id) || null;
    setSelectedProcedencia(procedencia);
    openDialog();
  };

  const handleCreate = () => {
    setSelectedProcedencia(null);
    openDialog();
  };

  const handleClose = () => {
    closeDialog();
  };

  const handleSave = async (updatedProcedencia: ProcedenciaRequest) => {
    try {
      await saveProcedencia(updatedProcedencia);
    } catch (error) {
      console.error('Error saving procedencia:', error);
    }
  };

  if (loading) {
    return (
      <ListLayoutPanel>
        <PageTitle
          title="Procedencias"
          subtitle="Gestión de procedencias"
          actions={
            <AddButton
              text="Nueva Procedencia"
              onClick={handleCreate}
            />
          }
        />
        <CircularProgress />
      </ListLayoutPanel>
    );
  }

  if (error) {
    return (
      <ListLayoutPanel>
        <PageTitle
          title="Procedencias"
          subtitle="Gestión de procedencias"
          actions={
            <AddButton
              text="Nueva Procedencia"
              onClick={handleCreate}
            />
          }
        />
        <Alert
          severity="error"
          onClose={clearError}
        >
          {error}
        </Alert>
      </ListLayoutPanel>
    );
  }

  return (
    <ListLayoutPanel>
      <PageTitle
        title="Procedencias"
        subtitle="Gestión de procedencias"
        actions={
          <AddButton
            text="Nueva Procedencia"
            onClick={handleCreate}
          />
        }
      />

      <DataGrid
        columns={columns}
        rows={procedencias}
        getRowId={(row: Procedencia) => row.idProcedencia}
      />

      <EditProcedencia
        open={dialogOpen}
        procedencia={selectedProcedencia}
        onClose={handleClose}
        onSave={handleSave}
        title={selectedProcedencia ? "Editar Procedencia" : "Nueva Procedencia"}
      />
    </ListLayoutPanel>
  );
};

export default ProcedenciasPage;
